import { FC, useContext } from "react";

import { Typography, useTheme } from "@mui/material";
import { FormType } from "@simplyk/common";

import { FormButton } from "../../../components/forms-design-system/FormButton";
import { PaymentErrorProvider } from "../../../components/PaymentElement/PaymentErrorContext";
import { FormUserAgreement } from "../../../components/UserAgreement/FormUserAgreement";
import { useFrontendAuctionContext } from "../../../contexts/FrontendAuctionContext";
import { FrontendFormContext } from "../../../contexts/FrontendFormContext";
import { useTranslate } from "../../../hooks/useTranslate";
import { useFormV2Context } from "../context/FormV2Context";
import { useUtmForm } from "../hooks/useUtmForm";

import { useBuyerInformationRenderPosition } from "./hooks/useBuyerInformationRenderPosition";
import { FormV2BuyerInformationSection } from "./sections/FormV2BuyerInformationSection";
import { FormV2BuyerQuestionsSection } from "./sections/FormV2BuyerQuestionsSection";
import { FormV2PaymentSection } from "./sections/FormV2PaymentSection";
import { FormV2SummarySection } from "./sections/SummarySection/FormV2SummarySection";
import { SubmitCommandButton } from "./SubmitButton/SubmitCommandButton";
import { SubmitFreeTicketingButton } from "./SubmitButton/SubmitFreeTicketingButton";
import { SubmitNewBidsButton } from "./SubmitButton/SubmitNewBidsButton";

import { useThemedFormZeffyLogoUrl } from "@/features/FormV2/hooks/useThemedFormZeffyLogoUrl";
import { ThemedFormZeffyLogo } from "@/features/FormV2/sharedComponents/ThemedFormZeffyLogo";
import { FormV2Step2Layout } from "@/features/FormV2/Step2/FormV2Step2Layout";
import { useTrackThemedFormLogoClicked } from "@/features/FormV2/tracking/useTrackThemedFormLogoClicked";
import { ChevronLeft } from "@/icons/outlined";

export const FormV2Step2: FC = () => {
  const { t } = useTranslate();
  const theme = useTheme();
  const formV2Context = useFormV2Context();
  const { type: formType, formCategory, formObject, setCurrentStep, isAuction } = formV2Context;
  const frontendFormContext = useContext(FrontendFormContext);
  const { isEmbed, displayedFormAmount } = frontendFormContext;
  const utm = useUtmForm();
  const { hasAlreadyBid } = useFrontendAuctionContext();

  const onTrackLogoClick = useTrackThemedFormLogoClicked({
    formType,
    formCategory,
    country: formObject.organization?.country,
    formStep: 2,
  });

  const logoHref = useThemedFormZeffyLogoUrl(utm);

  const isFreeTicket = displayedFormAmount === 0 && formType === FormType.Ticketing;

  const buyerInfoRenderPosition = useBuyerInformationRenderPosition();

  const weeklySupportersCount = formObject.weeklySupportersCount;
  const organizationName = formObject.organization?.name;
  const shouldDisplayPeerCredibilityMessage = weeklySupportersCount && weeklySupportersCount > 5;

  return (
    <PaymentErrorProvider>
      <FormV2Step2Layout
        buyerInformationPosition={buyerInfoRenderPosition}
        isEmbed={isEmbed}
        slots={{
          backButton: (
            <FormButton variant="outline" onClick={() => setCurrentStep("selection")} startIcon={<ChevronLeft />}>
              {t("ticketing", "back")}
            </FormButton>
          ),
          peerCredibilityMessage: shouldDisplayPeerCredibilityMessage && (
            <Typography variant="subtitle2" marginBottom={2} sx={{ color: theme.palette.forms.text.brand }}>
              {t("ticketing", "peerCredibilityMessage", {
                count: weeklySupportersCount,
                organizationName,
              })}
            </Typography>
          ),
          buyerInformation: <FormV2BuyerInformationSection />,
          buyerQuestions: <FormV2BuyerQuestionsSection />,
          summarySection: <FormV2SummarySection />,
          paymentSection: <FormV2PaymentSection />,
          submitButton: (
            <>
              {!isFreeTicket && !hasAlreadyBid && <SubmitCommandButton />}
              {isAuction && hasAlreadyBid && <SubmitNewBidsButton />}
              {isFreeTicket && <SubmitFreeTicketingButton />}
            </>
          ),
          userAgreement: <FormUserAgreement />,
          zeffyLogo: <ThemedFormZeffyLogo href={logoHref} onClick={onTrackLogoClick} />,
        }}
      />
    </PaymentErrorProvider>
  );
};
